import React from 'react';
import { Box, Card, Skeleton } from '@mui/material';

/**
 * A section's outline while its data loads, in place of a spinner.
 *
 * A spinner or thin progress bar followed by the real card makes the page jump as each section
 * arrives; an outline the same shape as the section holds the layout still and reads as faster.
 * Mirrors SectionCard: title, a line of description, then `rows` of content (or a chart block).
 */
export const SectionSkeleton: React.FC<{
  rows?: number;
  chart?: boolean;
  frameless?: boolean;
}> = ({ rows = 3, chart = false, frameless = false }) => {
  const content = (
    <>
      <Skeleton variant="text" width="35%" sx={{ fontSize: '1.25rem' }} />
      <Skeleton variant="text" width="65%" sx={{ mb: 2 }} />
      {chart
        ? <Skeleton variant="rounded" height={240} />
        : Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={36} sx={{ mb: 1 }} />
        ))}
    </>
  );
  if (frameless) return <Box sx={{ mb: 3 }} aria-busy="true">{content}</Box>;
  return (
    <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }} aria-busy="true">
      {content}
    </Card>
  );
};

/** The whole Overview tab's shape — four tiles, then a chart — for the first load of Stats. */
export const StatsSkeleton: React.FC = () => (
  <Box aria-busy="true">
    <Skeleton variant="text" width={120} sx={{ fontSize: '2rem' }} />
    <Skeleton variant="text" width="45%" sx={{ mb: 2 }} />
    <Skeleton variant="rounded" height={40} sx={{ mb: 3 }} />
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
      {[0, 1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={120} />)}
    </Box>
    <SectionSkeleton chart />
  </Box>
);
